import { z } from 'zod';
import { WorkflowDefinitionSchema } from '@/types/workflowDefinition';

// =============================================================================
// CONSTANTS
// =============================================================================

/** Template provenance — built-in (hand-crafted) vs community (synced from upstream) */
export const TEMPLATE_SOURCES = ['built-in', 'community'] as const;
export type TemplateSource = (typeof TEMPLATE_SOURCES)[number];

/** All supported template gallery categories */
export const TEMPLATE_CATEGORIES = ['automation', 'ai'] as const;

export type TemplateCategory = (typeof TEMPLATE_CATEGORIES)[number];

// =============================================================================
// UPSTREAM TYPES (Azure/LogicAppsTemplates GitHub repo)
// =============================================================================

/** Root-level manifest for a template in the upstream repo (<id>/manifest.json) */
export interface UpstreamRootManifest {
  id: string;
  title: string;
  summary: string;
  description?: string;
  skus: string[];
  workflows: Record<string, { name: string }>;
  featuredConnectors?: Array<{ id: string; kind?: string; name?: string }>;
  details?: { By?: string; Type?: string; Trigger?: string; Category?: string };
  tags?: string[];
}

/** Workflow-level manifest (<id>/default/manifest.json) */
export interface UpstreamWorkflowManifest {
  id: string;
  title: string;
  summary: string;
  description?: string;
  prerequisites?: string;
  kinds?: string[];
  artifacts?: Array<{ type: string; file: string }>;
  images?: { light: string; dark: string };
  parameters: Array<{
    name: string;
    displayName?: string;
    type: string;
    description?: string;
    required?: boolean;
    default?: unknown;
  }>;
  connections: Record<string, { connectorId: string; kind: string }>;
}

/** Flat workflow definition from <id>/default/workflow.json (no definition wrapper) */
export interface UpstreamWorkflowDefinition {
  $schema?: string;
  triggers: Record<string, unknown>;
  actions: Record<string, unknown>;
  contentVersion: string;
  outputs?: Record<string, unknown>;
  parameters?: Record<string, unknown>;
}

// =============================================================================
// SCHEMAS
// =============================================================================

/**
 * Describes a single connection that a template requires.
 * Templates declare *requirements* (connector type + API ID), not live
 * connection references — the user satisfies requirements at deploy time.
 * Display names and other metadata are fetched at runtime based on apiId.
 */
export const templateConnectionRequirementSchema = z.object({
  /**
   * Connection type: 'shared' for managed APIs, 'inapp' for built-in connectors, 'agent' for agent connections.
   */
  connectorType: z.enum(['shared', 'inapp', 'agent']),

  /**
   * API identifier path, e.g. '/managedApis/sql' or '/serviceProviders/AzureBlob'.
   * Used to look up connector metadata (display name, icon, etc.) at runtime.
   */
  apiId: z.string().min(1),

  /**
   * Connection reference key used in the workflow definition.
   * This is the key in the connections object (e.g., 'sql_myWorkflow').
   * When provided, placeholder connections use this exact key.
   */
  referenceKey: z.string().min(1).optional(),
});

export type TemplateConnectionRequirement = z.infer<typeof templateConnectionRequirementSchema>;

/**
 * Metadata used when rendering a template card in the gallery.
 * Does not include the full workflow definition — kept small for list views.
 */
export const templateManifestSchema = z.object({
  /** Stable, URL-safe identifier for this template (e.g. "approval-email-teams"). */
  id: z.string().min(1),

  /** Human-readable template name displayed in the gallery. */
  name: z.string().min(1),

  /** One-to-two sentence description of what the template does. */
  description: z.string().min(1),

  /**
   * Gallery categories used for filtering. A template can belong to one or
   * more categories — the gallery shows it under each of them. Always a
   * non-empty array; recipes that supply a bare string are coerced upstream
   * by the recipe schema.
   */
  category: z.array(z.enum(TEMPLATE_CATEGORIES)).min(1),

  /**
   * Semantic version string (e.g. "1.0.0").
   * Bump on breaking changes to the workflow definition.
   */
  version: z.string().regex(/^\d+\.\d+\.\d+$/, 'version must be in semver format (x.y.z)'),

  /**
   * Free-form search / filter tags (e.g. ["email", "approval", "teams"]).
   * All values are lower-cased at validation time.
   */
  tags: z.array(z.string().min(1).transform((tag) => tag.toLowerCase())).default([]),

  /** Connections this template requires. */
  connectionRequirements: z.array(templateConnectionRequirementSchema).default([]).refine(
    (reqs) => new Set(reqs.map(r => r.apiId)).size === reqs.length,
    { message: 'connectionRequirements must have unique apiId values' }
  ),

  /**
   * Optional ordering hint for the "Featured" row in the gallery.
   * Lower numbers appear first. Omit to exclude from the featured row.
   */
  featuredOrder: z.number().int().nonnegative().optional(),

  /** Provenance: built-in (hand-crafted) or community (synced from upstream repo). */
  source: z.enum(TEMPLATE_SOURCES).default('built-in'),

  /** Connectors to display as icons on gallery cards. */
  featuredConnectors: z.array(
    z.object({
      id: z.string().min(1),
      displayName: z.string().min(1),
      /** Full URL or local path (e.g. /connectorIcons/request.svg) */
      iconUrl: z.string().min(1),
    })
  ).optional(),

  /** Optional preview image URL (light-theme) for upstream templates. */
  imageUrl: z.string().url().optional(),

  /** Optional dark-theme preview image URL. */
  imageDarkUrl: z.string().url().optional(),
});

export type TemplateManifest = z.infer<typeof templateManifestSchema>;

/**
 * Zod schema for connections.json structure embedded inside a template.
 * Mirrors the `ConnectionsConfiguration` type from logicApps.ts as a loose
 * schema — individual connection shapes are validated at apply time, not here.
 */
const templateConnectionsConfigSchema = z
  .object({
    managedApiConnections: z.record(z.string(), z.unknown()).optional(),
    serviceProviderConnections: z.record(z.string(), z.unknown()).optional(),
    agentConnections: z.record(z.string(), z.unknown()).optional(),
  })
  .passthrough();

/**
 * Zod schema for a single parameter definition embedded in a template.
 * Matches the `ParameterDefinition` shape from logicApps.ts.
 */
const templateParameterDefinitionSchema = z.object({
  type: z.string().min(1),
  value: z.unknown().optional(),
  metadata: z
    .object({
      description: z.string().optional(),
    })
    .optional(),
  // Extended fields for richer template parameter UI
  displayName: z.string().optional(),
  description: z.string().optional(),
  required: z.boolean().optional(),
  defaultValue: z.unknown().optional(),
  allowedValues: z.array(z.unknown()).optional(),
});

/**
 * Full template definition — manifest metadata plus the runtime artifacts
 * (workflow definition, connections skeleton, optional parameters).
 *
 * This is the shape of the static JSON files loaded from the template library.
 */
export const templateDefinitionSchema = templateManifestSchema.extend({
  /**
   * The complete workflow definition (workflow.json shape).
   * Validated against `WorkflowDefinitionSchema`.
   */
  workflowDefinition: WorkflowDefinitionSchema,

  /**
   * Skeleton connections.json for the template.
   * Connection IDs are placeholder values replaced when the user deploys.
   */
  connectionsConfiguration: templateConnectionsConfigSchema.optional(),

  /**
   * Optional parameters.json content bundled with the template.
   * Keys are parameter names; values follow `ParameterDefinition` shape.
   */
  parameters: z.record(z.string(), templateParameterDefinitionSchema).optional(),
});

export type TemplateDefinition = z.infer<typeof templateDefinitionSchema>;

// =============================================================================
// VALIDATION HELPER
// =============================================================================

/**
 * Validate an unknown value against `templateDefinitionSchema`.
 *
 * Returns a discriminated result:
 * - `{ success: true,  data: TemplateDefinition }` on success
 * - `{ success: false, errors: string[] }`          on failure
 *
 * @example
 * const result = validateTemplate(JSON.parse(rawJson));
 * if (result.success) {
 *   console.log(result.data.name);
 * } else {
 *   console.error(result.errors);
 * }
 */
export function validateTemplate(data: unknown): { success: true; data: TemplateDefinition } | { success: false; errors: string[] } {
  const result = templateDefinitionSchema.safeParse(data);

  if (result.success) {
    return { success: true, data: result.data };
  }

  const errors = result.error.issues.map((issue) => {
    const path = issue.path.length > 0 ? `${issue.path.join('.')}: ` : '';
    return `${path}${issue.message}`;
  });

  return { success: false, errors };
}

// =============================================================================
// Display helpers — shared across TemplateCard, TemplatePreview, etc.
// =============================================================================

export const CATEGORY_DISPLAY: Record<string, string> = {
  automation: 'Automation',
  ai: 'AI',
};
